<template>
  <div>
    <div v-if="loading">
      <p>carregando</p>
    </div>
    <div class="px-md-9 px-2" v-else>
      <p v-if="isMobile" class="text-button clear pl-2">
        Salas disponíveis
      </p>
      <p v-else class="text-h6 pl-2 mt-7">
        Buscar por salas disponíveis
      </p>
      <v-row>
        <v-col :cols="12" :md="4" v-if="!isMobile">
          <HandleMatrix
            :matrix="matrix_rate"
            :rowHeader="rows"
            :colHeader="cols"
            :max="rooms_qtd"
            :item="itemDefault"
            show-values-by-default
            @click-item="handleClick"
          />
        </v-col>
        <v-col :cols="12" :md="8">
          <div class="px-md-10 mt-md-8">
            <AssistentCard
              class="mb-3"
              text="Clique na tabela de horários ou use os filtros abaixo para visualizar as salas disponíveis."
            />

            <div class="filter-actions">
              <v-tooltip top>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    icon
                    small
                    color="primary"
                    :disabled="!hasActiveFilters"
                    aria-label="Limpar filtros"
                    v-bind="attrs"
                    v-on="on"
                    @click="clearFilters"
                  >
                    <v-icon>mdi-broom</v-icon>
                  </v-btn>
                </template>
                <span>Limpar filtros</span>
              </v-tooltip>
            </div>

            <div class="availability-filters">
              <v-select
                v-model="selected_day"
                :items="dayOptions"
                item-text="text"
                item-value="value"
                label="Dia"
                outlined
                dense
                prepend-inner-icon="mdi-calendar"
              ></v-select>
              <v-select
                v-model="selected_hour"
                :items="hourOptions"
                item-text="text"
                item-value="value"
                label="Horário"
                outlined
                dense
                prepend-inner-icon="mdi-clock-outline"
              ></v-select>
              <v-select
                label="Tipo de sala"
                v-model="room_type_selected"
                :items="rooms_types"
                outlined
                dense
                prepend-inner-icon="mdi-door-open"
                multiple
                chips
                deletable-chips
                placeholder="Todos os tipos de sala"
              ></v-select>
              <v-text-field
                label="Capacidade mínima"
                v-model.number="capacity"
                type="number"
                min="0"
                outlined
                dense
                prepend-inner-icon="mdi-account-group"
              ></v-text-field>
            </div>

            <template v-if="isMobile">
              <v-btn
                rounded
                outlined
                class="my-4"
                small
                @click.stop="
                  displayMatrixMobile = !displayMatrixMobile
                "
              >
                <v-icon small class="mr-2">mdi-calendar</v-icon>
                horários
                <v-icon small class="ml-2">{{
                  displayMatrixMobile
                    ? "mdi-chevron-up"
                    : "mdi-chevron-down"
                }}</v-icon></v-btn
              >
              <v-expand-transition>
                <HandleMatrix
                  v-show="displayMatrixMobile"
                  class="mb-4"
                  :matrix="matrix_rate"
                  :rowHeader="rows"
                  :colHeader="cols"
                  :max="rooms_qtd"
                  :item="itemDefault"
                  show-values-by-default
                  @click-item="handleClick"
                />
              </v-expand-transition>
            </template>

            <!-- ========================== display search result ================================= -->

            <div v-if="selectedTimeIndex">
              <p class="mb-3">
                <span class="text-button">{{ selectedTimeTitle }}</span>
              </p>

              <HandleRoomTable
                v-if="filter_search.length > 0"
                :rooms="filter_search"
                :displayMaxMsg="rooms_qtd == filter_search.length"
                @click-plus="handleClickPlus"
              />

              <div v-else class="empty-state">
                <v-img
                  class="empty-image"
                  contain
                  :src="notFoundImage"
                ></v-img>
                <p class="empty-title mb-1">
                  Nenhuma sala desalocada encontrada
                </p>
                <p class="empty-text mb-0">
                  Não existem ou não foram encontradas salas
                  desalocadas naquele horário com os filtros
                  informados.
                </p>
              </div>
            </div>

            <template v-else>
              <div class="select-time-state">
                <p class="text-button mb-1">
                  Selecione um horário para consultar disponibilidade
                </p>
                <p class="text-body-2 mb-0">
                  Use o filtro de horário ou clique em uma célula da
                  tabela para listar as salas desalocadas.
                </p>
              </div>
            </template>
          </div>
        </v-col>
      </v-row>
    </div>
  </div>
</template>

<script>
import HandleMatrix from "@/handles/paas/HandleMatrix.vue";
import HandleRoomTable from "@/handles/paas/HandleRoomTable.vue";
import AssistentCard from "@/components/AssistentCard.vue";

import { convertUFPBTimeFormatToIdx } from "@/utils/PaasOccupancyRate";

export default {
  name: "handleSearchEmpty",
  components: {
    HandleMatrix,
    HandleRoomTable,
    AssistentCard
  },
  props: {
    loading: Boolean,
    matrix_rate: Array,
    matrix_search_empty: Array,
    rooms_qtd: Number,
    rooms_types: Array,
    rows: Array,
    cols: Array
  },
  data() {
    return {
      selected_day: null,
      selected_hour: null,
      displayMatrixMobile: false,
      notFoundImage: require("@/assets/not-found.svg"),
      time: {},
      week_label: [
        "Domingo",
        "Segunda-feira",
        "Terça-feira",
        "Quarta-feira",
        "Quinta-feira",
        "Sexta-feira",
        "Sábado"
      ],
      room_type_selected: [],
      capacity: 0
    };
  },
  computed: {
    isMobile() {
      return this.$vuetify.breakpoint.width < 570;
    },
    itemDefault() {
      return this.selectedTimeIndex || [-1, -1];
    },
    dayOptions() {
      return this.cols.map((col, index) => {
        return {
          text: this.week_label[index + 1],
          value: index
        };
      });
    },
    hourOptions() {
      return this.rows
        .map((row, index) => {
          return {
            text: row,
            value: index
          };
        })
        .filter((_, index) => index < 16);
    },
    selectedTimeIndex() {
      if (this.selected_day === null || this.selected_hour === null)
        return null;

      return [Number(this.selected_hour), Number(this.selected_day)];
    },
    selectedTimeTitle() {
      if (!this.selectedTimeIndex) return "";

      const [i, j] = this.selectedTimeIndex;
      return `${this.week_label[j + 1]}, ${this.rows[i]}`;
    },
    selectedRooms() {
      if (!this.selectedTimeIndex) return [];

      const [i, j] = this.selectedTimeIndex;
      return this.matrix_search_empty[i][j] || [];
    },
    selectedRoomTypes() {
      return Array.isArray(this.room_type_selected)
        ? this.room_type_selected
        : this.room_type_selected
        ? [this.room_type_selected]
        : [];
    },
    hasActiveFilters() {
      return (
        this.selected_day !== null ||
        this.selected_hour !== null ||
        this.selectedRoomTypes.length > 0 ||
        Number(this.capacity || 0) > 0
      );
    },
    filter_search() {
      const minCapacity = Number(this.capacity || 0);

      if (!this.selectedRoomTypes.length) {
        return this.selectedRooms.filter(_ => _.capacidade >= minCapacity);
      }

      return this.selectedRooms.filter(
        _ =>
          _.capacidade >= minCapacity &&
          this.selectedRoomTypes.includes(_.tipo)
      );
    }
  },
  mounted() {
    this.getTime();
    if (!this.time.no_range) {
      this.selected_hour = this.time.idx[0];
      this.selected_day = this.time.idx[1];
    }
    setInterval(() => {
      this.getTime();
    }, 60000);
  },
  methods: {
    clearFilters() {
      this.selected_day = null;
      this.selected_hour = null;
      this.room_type_selected = [];
      this.capacity = 0;
    },
    handleClickPlus(idx) {
      this.$emit("click-plus", idx);
    },
    handleClick(i, j) {
      this.selected_hour = i;
      this.selected_day = j;
    },
    getTime() {
      let _time = new Date();
      let hour = _time.getHours();
      let min = _time.getMinutes();
      let day = _time.getDay();
      this.time = this.timeToUFPBFormat(hour, min, day);
    },
    timeToUFPBFormat(hour, min, day) {
      let _time = `${
        this.week_label[day]
      }, ${hour
        .toString()
        .padStart(2, "0")}:${min.toString().padStart(2, "0")}.`;
      let format = "";

      if (
        day == 0 ||
        hour < 7 ||
        hour >= 23 ||
        (hour == 22 && min > min > 10)
      )
        return {
          no_range: true,
          value: format,
          time: _time
        };

      format += day + 1;

      switch (true) {
        case hour < 13:
          format += "M" + (hour - 6);
          break;
        case hour < 19:
          format += "T" + (hour - 12);
          break;
        default:
          format +=
            "N" + Math.ceil(((hour - 19) * 60 + min + 1) / 50);
      }

      let idx = convertUFPBTimeFormatToIdx(format);
      idx = [idx[1][0], idx[0][0]];

      return {
        no_range: false,
        value: format,
        time: _time,
        idx
      };
    }
  }
};
</script>

<style scoped>
.clear {
  clear: both;
}

.filter-actions {
  display: flex;
  justify-content: flex-end;
  margin-bottom: 4px;
}

.availability-filters {
  display: grid;
  gap: 12px;
  grid-template-columns:
    minmax(120px, 0.8fr)
    minmax(120px, 0.8fr)
    minmax(160px, 1fr)
    minmax(140px, 0.75fr);
  margin-bottom: 8px;
}

.empty-state {
  align-items: center;
  border: 1px solid #e6eef8;
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  margin-top: 8px;
  padding: 32px 20px;
  text-align: center;
}

.empty-image {
  max-width: 260px;
  width: 60%;
}

.empty-title {
  color: #0346b6;
  font-size: 18px;
  font-weight: 800;
}

.empty-text {
  color: #5f6874;
  font-size: 14px;
  max-width: 520px;
}

@media only screen and (max-width: 760px) {
  .availability-filters {
    grid-template-columns: 1fr;
  }
}
</style>
